import { PREVIEW_PAGE } from 'client/constants/routes';
import config from 'application-config';
import { IS_DEVEL } from 'utils/environment';
import {
	buildClientBackendUrl,
	buildStoryPathBase,
	getActivePathPrefix,
	pickPublishPathPrefix,
} from 'utils/path-prefix';
import { prependUrlProtocol } from 'utils/url/prepend-url-protocol';
import { MAX_ORGANIZATION_ID } from 'common/constants/organizations';
import { CARD_PATH_SEGMENT, PARAMS, SEARCH_PARAMS } from 'common/constants/url-params';

// Simple native replacement for lodash.get
function get(obj: any, path: string): any {
	return path.split('.').reduce((current, key) => current?.[key], obj);
}

interface PublishedUrlProps {
	clientStoryId: string;
	organizationId?: string | null;
	cardId?: string;
	customDomain?: string;
	/** Path prefixes from org domain API (`prefixes`). Used with customDomain only. */
	pathPrefixes?: string[] | null;
	storycardsDomain?: string;
	noProtocol?: boolean;
	// by default, client app doing a redirect to first card in story. this flag will cancel this behaviour.
	preventRedirect?: boolean;
}

function withDynamicHostname(urlParam: string) {
	const pathPrefix = getActivePathPrefix();
	const result = buildClientBackendUrl({
		configuredApiUrl: urlParam,
		hostname: window.location.hostname,
		pathPrefix,
	});
	if (pathPrefix) {
		// eslint-disable-next-line no-console
		console.info('[path-prefix] clientBackend', result);
	}
	return result;
}

export const addEndingSlash = (val = '') => (val.match(/\/$/) ? val : `${val}/`);

const domains = {
	FRONTEND_ADMIN: 'frontend.admin',
	FRONTEND_CLIENT: 'frontend.client',
	BACKEND_ADMIN: 'backend.admin',
	BACKEND_CLIENT: 'backend.client',
};

type ConfigDomain = {
	hostname: string;
	port?: number;
};

type Config = {
	GA_SC_GENERAL: string;
	api: {
		chat?: string;
		adminBackendAlt: string;
		adminBackend: string;
		adminBackendV2: string;
		adminBackendV2R: string;
		adminBackendV2RSubdomain: string;
		clientBackend: string;
	};
	domains: {
		backend: {
			admin: ConfigDomain;
			client: ConfigDomain;
		};
		frontend: {
			admin: ConfigDomain;
			client: ConfigDomain;
		};
	};
	cname: string;
};
/**
 * UrlHelper class to help get resource url for our application
 * Singleton
 */
class UrlHelper {
	config: Config;

	/**
	 * Create UrlHelper instance
	 *
	 * @param configParam {Object} url configuration
	 * @return UrlHelper {Object} instance
	 */
	constructor(configParam: Config) {
		this.config = configParam;
	}

	get cname() {
		return this.config.cname;
	}

	/**
	 * Get backend host url
	 * @return {string}  admin host url
	 */
	get clientBackend() {
		return IS_DEVEL ? this.config.api.clientBackend : withDynamicHostname(this.config.api.clientBackend);
	}

	get chat() {
		return this.config.api.chat;
	}

	get adminBackendAlt() {
		return this.config.api.adminBackendAlt;
	}

	/**
	 * Get admin host url
	 * @return {string}  admin host url
	 */
	get adminBackend() {
		return this.config.api.adminBackend;
	}

	get adminBackendV2() {
		return this.config.api.adminBackendV2;
	}

	get adminBackendV2R() {
		return this.config.api.adminBackendV2R;
	}

	get adminBackendV2RSubdomain() {
		return this.config.api.adminBackendV2RSubdomain;
	}

	/**
	 * Get client host url
	 * @return {string}  client host url
	 */
	get client() {
		return this.getDomain(domains.FRONTEND_CLIENT);
	}

	/**
	 * Get admin host url
	 * @return {string}  admin host url
	 */
	get admin() {
		return this.getDomain(domains.FRONTEND_ADMIN);
	}

	/**
	 * @example getDomain('FRONTEND_ADMIN')
	 */
	getDomain(domain: string): string {
		const domainProps = get(config.domains, domain);
		return domainProps.port ? `${domainProps.hostname}:${domainProps.port}` : domainProps.hostname;
	}

	/**
	 * Get url of published story
	 */
	getPublishedUrl(props: PublishedUrlProps) {
		const {
			clientStoryId,
			organizationId,
			cardId,
			customDomain,
			pathPrefixes,
			storycardsDomain,
			noProtocol,
			preventRedirect,
		} = props;
		let domain: string;
		let basePath: string;

		if (organizationId === MAX_ORGANIZATION_ID) {
			domain = 'story.max.co.il/';
			basePath = clientStoryId.split('/').pop()!;
		} else if (customDomain) {
			const [, storyId] = clientStoryId.split('/');
			domain = addEndingSlash(customDomain);
			basePath = addEndingSlash(
				buildStoryPathBase({
					storySegment: storyId,
					pathPrefix: pickPublishPathPrefix(pathPrefixes),
				}).replace(/^\//, '')
			);
		} else if (storycardsDomain) {
			domain = addEndingSlash(storycardsDomain);
			basePath = addEndingSlash(clientStoryId);
		} else if (IS_DEVEL) {
			domain = addEndingSlash('story.dev.story-cards.com');
			basePath = addEndingSlash(clientStoryId);
		} else {
			domain = addEndingSlash(this.getDomain(domains.FRONTEND_CLIENT));
			basePath = addEndingSlash(clientStoryId);
		}

		let url = `${domain}${basePath}`;

		if (cardId) {
			// Matches /c/ followed by any non-slash characters, optionally ending with /
			const cardRegex = new RegExp(`/${CARD_PATH_SEGMENT}/[^/]+/?`, 'gi');
			const cardPath = cardId ? addEndingSlash(`${CARD_PATH_SEGMENT}/${cardId}`) : '';
			if (cardRegex.test(url)) url = url.replace(cardRegex, cardPath);
			else url = addEndingSlash(url) + cardPath;
		}

		const queryParams: string[] = [];
		if (cardId) queryParams.push(`${PARAMS.CARD_ID}=${cardId}`);
		if (preventRedirect) queryParams.push(`${PARAMS.REDIRECT}=0`);
		const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';

		// Append query string
		url += queryString;

		return noProtocol ? url : prependUrlProtocol(url, 'https');
	}

	getDevEmbedUrl = ({ clientStoryId }: { clientStoryId: string }) => {
		const embedDevUrl = `${window.location.origin}${PREVIEW_PAGE}`;
		const storyIdParam = `${PARAMS.STORY_ID}=${clientStoryId.split('/')[1]}`;
		return `${embedDevUrl}?${SEARCH_PARAMS.EMBED_DEV}&${storyIdParam}`;
	};
}

const UrlHelperInstance = new UrlHelper(config);

export const location = {
	client: {
		/**
		 * Is client app embedded in admin dashboard
		 * @desc is [cardEditor | cardPreview | embedPreview]
		 */
		isPreview:
			document.referrer &&
			document.referrer !== window.location.origin &&
			document.referrer.includes(UrlHelperInstance.admin),

		isPreviewRoute: window.location.pathname.endsWith(PREVIEW_PAGE),

		isEmbedDevPreview: () => {
			return (
				location.client.isPreviewRoute &&
				new URLSearchParams(window.location.search).has(SEARCH_PARAMS.EMBED_DEV)
			);
		},

		/**
		 * @desc is opened by screenshot API
		 */
		isScreenshot: () => window.location.href.match(/[&|?]screenshot/) !== null,

		isCardEditorPreview: () => {
			return (
				location.client.isPreviewRoute &&
				new URLSearchParams(window.location.search).has(SEARCH_PARAMS.EDITOR_PREVIEW)
			);
		},
	},
	admin: {
		isAdminDashboard: () => {
			return window.location.host === UrlHelperInstance.admin && !location.client.isPreviewRoute;
		},
	},
};

export default UrlHelperInstance;
